import { init } from '@nemuru-eng/checkout-js';
import {
  CheckoutSession,
  getAgentById,
  getLocaleByPurchaseCountry,
  LoanStatusExternal,
  PaymentMethodType,
} from '@nemuru-eng/js-api-client';
import { useParams } from 'react-router-dom';

import { usePaymentMethods } from '@/components/modules/Application/components/PaymentMethod/_shared/hooks/usePaymentMethods';
import { GetPricingsService } from '@/components/modules/Application/components/PaymentMethod/_shared/services/GetPricingsService';
import { useCheckoutSessionContext } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/contexts/CheckoutSessionContext';
import { CheckoutSessionDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/entities/CheckoutSessionDetailEntity';
import { AbstractLoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/entities/PaymentMethodDetail/AbstractLoanPaymentMethodDetailEntity';
import { ENV_ENV } from '@/config/envVars';
import { useGetCheckoutSessionById } from '@/hooks/requests/checkoutSession/useGetCheckoutSessionById';

const pricingsService = GetPricingsService.getInstance();

const getFirstPaymentMethodTypeByTabOrder = (checkoutSession: CheckoutSession): PaymentMethodType | undefined =>
  [...checkoutSession.paymentMethods].sort((a, b) => a.type.localeCompare(b.type))[0]?.type;

export const resolveSelectedPaymentMethodType = (
  checkoutSession: CheckoutSession,
  currentSelectedPaymentMethodType?: PaymentMethodType,
  previousSessionSelectedPaymentMethodType?: PaymentMethodType,
): PaymentMethodType | undefined => {
  const sessionSelected = checkoutSession.selectedPaymentMethodType ?? undefined;
  if (sessionSelected && sessionSelected !== previousSessionSelectedPaymentMethodType) {
    return sessionSelected;
  }
  return currentSelectedPaymentMethodType ?? sessionSelected ?? getFirstPaymentMethodTypeByTabOrder(checkoutSession);
};

export const useCheckoutSession = () => {
  const { checkoutSessionEntity, paymentMethodDetailEntities, selectedPaymentMethodType, update } =
    useCheckoutSessionContext();

  const { checkoutSessionId } = useParams();
  const { checkoutSession, resetCache } = useGetCheckoutSessionById(checkoutSessionId);
  const { areAllPaymentMethodEntitiesSetup } = usePaymentMethods(); // FIXME: remove dependency to Application

  const createCheckoutSessionEntity = async () => {
    if (!checkoutSession) {
      throw new Error('Missing checkoutSession in createCheckoutSessionEntity.');
    }

    const agent = await getAgentById({ agentId: checkoutSession.agentId });

    const checkoutSessionEntity = new CheckoutSessionDetailEntity(checkoutSession, agent);

    update({
      checkoutSessionEntity,
      selectedPaymentMethodType: resolveSelectedPaymentMethodType(checkoutSession),
    });

    // Asynchronously create payment method detail entities
    if (checkoutSessionEntity.requiresFetchingPricings()) {
      await fetchPricings();
    }

    const paymentMethodDetailEntities = await checkoutSessionEntity.createPaymentMethodDetailEntities();

    update({ paymentMethodDetailEntities });

    return checkoutSessionEntity;
  };

  const fetchPricings = async () => {
    if (!checkoutSession) {
      throw new Error('Missing checkoutSession in initPricings.');
    }

    // TODO: Try to avoid doing the pricingService.execute, as it is time consuming...
    await init(checkoutSession.agentId, {
      env: ENV_ENV,
      locale: getLocaleByPurchaseCountry(checkoutSession.purchaseCountry),
    });
    await pricingsService.execute(checkoutSession.agentId);
  };

  const waitUntilPaymentIntentStatus = async (paymentMethodId: string, status: LoanStatusExternal): Promise<void> => {
    if (!checkoutSessionEntity) throw new Error('Missing checkoutSessionEntity in waitUntilPaymentIntentStatus.');
    await checkoutSessionEntity.waitUntilPaymentIntentStatus(paymentMethodId, status);
    update({ checkoutSessionEntity });
  };

  const syncPaymentMethodsWithLender = async () => {
    if (!paymentMethodDetailEntities || paymentMethodDetailEntities.length === 0) {
      return;
    }

    // Sync all loan payment method detail entities with lender in parallel
    await Promise.all(
      paymentMethodDetailEntities
        .filter((pm) => pm instanceof AbstractLoanPaymentMethodDetailEntity)
        .map((pm) => (pm as AbstractLoanPaymentMethodDetailEntity).syncWithLender()),
    );
  };

  const refreshCheckoutSessionAndPaymentMethodEntities = async () => {
    update({ refreshing: true });
    if (!checkoutSessionEntity) throw new Error('Missing checkoutSessionEntity in updateCheckoutSession.');
    const previousSelectedPaymentMethodType =
      checkoutSessionEntity.checkoutSession.selectedPaymentMethodType ?? undefined;
    await checkoutSessionEntity.refresh();
    await resetCache?.(checkoutSessionEntity.checkoutSession);
    await syncPaymentMethodsWithLender();
    const updatedPaymentMethodDetailEntities = await checkoutSessionEntity.createPaymentMethodDetailEntities();

    const selectedPaymentMethodTypeAfterRefresh = resolveSelectedPaymentMethodType(
      checkoutSessionEntity.checkoutSession,
      selectedPaymentMethodType,
      previousSelectedPaymentMethodType,
    );

    update({
      checkoutSessionEntity,
      paymentMethodDetailEntities: updatedPaymentMethodDetailEntities,
      selectedPaymentMethodType: selectedPaymentMethodTypeAfterRefresh,
      refreshing: false,
    });
  };

  return {
    areAllPaymentMethodEntitiesSetup,
    checkoutSessionEntity,
    createCheckoutSessionEntity,
    paymentMethodDetailEntities,
    refreshCheckoutSessionAndPaymentMethodEntities,
    waitUntilPaymentIntentStatus,
  };
};
